import { useTranslation } from "react-i18next";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AdvancedFilters, FilterState } from "@/components/AdvancedFilters";

const ALL_CATEGORIES = [
  { value: "Målning", labelKey: "categories.painting" },
  { value: "Skulptur", labelKey: "categories.sculpture" },
  { value: "Fotografi", labelKey: "categories.photography" },
  { value: "Digital konst", labelKey: "categories.digitalArt" },
  { value: "Illustration", labelKey: "categories.illustration" },
  { value: "Grafik", labelKey: "categories.graphics" },
  { value: "Övrigt", labelKey: "categories.other" },
];

interface ExploreFiltersProps {
  category: string;
  setCategory: (value: string) => void;
  selectedArtist: string;
  setSelectedArtist: (value: string) => void;
  artworkType: "all" | "original" | "print";
  setArtworkType: (value: "all" | "original" | "print") => void;
  sortBy: string;
  setSortBy: (value: string) => void;
  advancedFilters: FilterState;
  setAdvancedFilters: (filters: FilterState) => void;
  artists: { id: string; username: string | null; full_name: string | null }[] | undefined;
  availableCategories: string[];
}

export function ExploreFilters({
  category,
  setCategory,
  selectedArtist,
  setSelectedArtist,
  artworkType,
  setArtworkType,
  sortBy,
  setSortBy,
  advancedFilters,
  setAdvancedFilters,
  artists,
  availableCategories,
}: ExploreFiltersProps) {
  const { t } = useTranslation();
  
  const visibleCategories = ALL_CATEGORIES.filter(cat => 
    availableCategories.includes(cat.value)
  );

  return (
    <div className="mb-3 sm:mb-4 md:mb-8">
      <div className="grid grid-cols-2 gap-2 sm:gap-3 sm:flex sm:flex-row">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="h-10 sm:h-12 text-sm sm:text-base">
            <SelectValue placeholder={t('explore.category')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('explore.allCategories')}</SelectItem>
            {visibleCategories.map((cat) => (
              <SelectItem key={cat.value} value={cat.value}>
                {t(cat.labelKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedArtist} onValueChange={setSelectedArtist}>
          <SelectTrigger className="h-10 sm:h-12 text-sm sm:text-base">
            <SelectValue placeholder={t('explore.artist')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('explore.allArtists')}</SelectItem>
            {artists?.map((artist) => (
              <SelectItem key={artist.id} value={artist.id}>
                {artist.username || artist.full_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={artworkType} onValueChange={(value: "all" | "original" | "print") => setArtworkType(value)}>
          <SelectTrigger className="h-10 sm:h-12 text-sm sm:text-base">
            <SelectValue placeholder={t('explore.type')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('explore.allTypes')}</SelectItem>
            <SelectItem value="original">{t('explore.originals')}</SelectItem>
            <SelectItem value="print">{t('explore.printsAndPosters')}</SelectItem>
          </SelectContent>
        </Select>

        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="h-10 sm:h-12 text-sm sm:text-base">
            <SelectValue placeholder={t('explore.sort')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="popular">{t('explore.mostPopular')}</SelectItem>
            <SelectItem value="newest">{t('explore.newestFirst')}</SelectItem>
            <SelectItem value="price_low">{t('explore.priceLowToHigh')}</SelectItem>
            <SelectItem value="price_high">{t('explore.priceHighToLow')}</SelectItem>
          </SelectContent>
        </Select>

        <AdvancedFilters 
          onFiltersChange={setAdvancedFilters}
          initialFilters={advancedFilters}
        />
      </div>
    </div>
  );
}